import CloudStorage from '@/cloudstorage/CloudStorage'
import { DeviceTransmissionService } from '@core/transmission/DeviceTransmissionService'
import { DeviceTaskError, TaskError, TaskState, TransmissionTaskType } from '@core/transmission/ITask'
import { HttpRequestCancelToken } from '@core/HttpRequestCancelToken'
import { TransmissionState } from '@core/databus/IMessage'
import { AbsUploadTask } from '@/core/transmission/AbsUploadTask'
import { TransmissionDataBusProxy } from './TransmissionDataBusProxy'
import { ILLEGAL_FILE_SIZE } from './AbsTask'
import { STATUS_CODE_NATIVE_SERVICE_BIND_FAILED } from '@/netadapters/EndpointAdapter'
import { isEmpty } from 'lodash-es'
import { UPLOAD_MAX_SIZE_SINGLE_FILE_IN_BYTES } from '../FileUtils'
import type { TransmissionNoticeData } from '../databus/DataSyncManager'

/**
 * 通过ocs传输文件到移动端
 */
export class UploadTaskWithCloud extends AbsUploadTask {
  #multipartId: string | undefined = undefined //已传输完成的判断id
  #isTransmissionToOCS = true // trueL 上传到ocs阶段 false:下载到手机阶段
  #uploadToOcsTransmitted = 0
  #uploadToOcsTotal = 0
  #downloadToDeviceTransmitted = 0
  #downloadFromDeviceTotal = 0
  #fileId = ''

  constructor(deviceId: string, targetPath: string, file: File, relativePath: string, appId: string) {
    super(deviceId, targetPath, file, false, relativePath, appId)
    this.#downloadFromDeviceTotal = file.size
  }

  async start(): Promise<void> {
    const device = this._getDevice()

    if (!device) {
      this._terminateDueToDeviceNotFound()

      return
    }

    try {
      if (!this._noticeId) {
        const notice = await TransmissionDataBusProxy.getInstance().appendNotice({
          appId: this._appId,
          isUpload: true,
          targetDevice: this._deviceId,
          targetPath: this._targetPath,
          fileName: this._fileName,
          fileSize: this._fileSize,
          transmitted: 0,
          state: TransmissionState.ON_GOING,
          ...(device.isSupportZip ? { businessType: TransmissionTaskType.UPLOAD } : {}),
        })

        console.log(
          `[Transmission] fileName: ${this._fileName} ${new Date().toLocaleString()} `,
          'upload appendNotice notice:',
          notice
        )

        if (!notice.data) {
          this._failedReason = TaskError.CREATE_NOTICE_FAILED
          this._taskScheduler?.setTaskError(this)

          return
        }
        this._preTransmissionNotice = JSON.stringify(notice.data)

        this._noticeId = notice.data.id
      }

      this._requestCancelToken = new HttpRequestCancelToken()

      if (this._file!.size > UPLOAD_MAX_SIZE_SINGLE_FILE_IN_BYTES) {
        await CloudStorage.INSTANCE.uploadFileWithMultipart(
          this._file!,
          this._deviceId,
          this._targetPath,
          this._progressListener,
          this._requestCancelToken
        )
      } else {
        await CloudStorage.INSTANCE.uploadFile(
          this._file!,
          this._deviceId,
          this._targetPath,
          this._progressListener,
          this._requestCancelToken
        )
      }

      this.canChangeState = true
    } catch (error) {
      console.log(`[Transmission] fileName: ${this._fileName} ${new Date().toLocaleString()} `, 'upload error:', error)
      this._failedReason = TaskError.TRANSFER_FAILED
      this._handleError(error)
    }
  }

  /**
   * 暂停存在两个阶段，1.上传ocs阶段 2.设备端下载
   * @returns
   */
  async pause(): Promise<void> {
    try {
      if (this.#isTransmissionToOCS) {
        this._requestCancelToken?.cancel()
      } else {
        const device = this._getDevice()

        if (!device) {
          this._terminateDueToDeviceNotFound()

          return
        }

        if (!this.canChangeState) return

        this.canChangeState = false

        this._taskScheduler?.updateTask(this)

        const DeviceTransmissionResponse = await DeviceTransmissionService.pause(
          device,
          this._noticeId,
          false,
          TransmissionTaskType.UPLOAD
        )

        console.log(
          `[Transmission] ${new Date().toLocaleString()}  fileName:`,
          this._fileName,
          'DeviceTransmissionResponse:pause',
          DeviceTransmissionResponse
        )

        if (DeviceTransmissionResponse.isSuccess) {
          this.setTimer()
        } else {
          if (DeviceTransmissionResponse.RemoteServiceResponse?.statusCode === STATUS_CODE_NATIVE_SERVICE_BIND_FAILED) {
            this._failedReason = TaskError.NATIVE_SERVICE_BIND_FAILED
          } else {
            this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
          }

          this._taskScheduler?.setTaskError(this)
          this.clearTimer()
        }
      }
    } catch (error) {
      console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'error:pause', error)
      this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED

      this._handleError(error)
    }
  }

  async resume(): Promise<void> {
    try {
      if (this.#isTransmissionToOCS && isEmpty(this.#fileId)) {
        if (this.#multipartId) {
          this._requestCancelToken = new HttpRequestCancelToken()

          await CloudStorage.INSTANCE.resumeUploadingFileWithMultipart(
            this.#multipartId,
            this._file!,
            this._transmitted,
            this._progressListener,
            this._requestCancelToken
          )
        } else {
          this.start()
        }
      } else if (this.#isTransmissionToOCS && this.#fileId) {
        this.onCompleted(this.#fileId)
      } else {
        const device = this._getDevice()

        if (!device) {
          this._terminateDueToDeviceNotFound()

          return
        }

        if (!this.canChangeState) return

        this.canChangeState = false

        this._taskScheduler?.updateTask(this)

        const DeviceTransmissionResponse = await DeviceTransmissionService.resume(
          device,
          this._noticeId,
          false,
          TransmissionTaskType.UPLOAD
        )

        console.log(
          `[Transmission] ${new Date().toLocaleString()}  fileName:`,
          this._fileName,
          'DeviceTransmissionResponse:resume',
          DeviceTransmissionResponse
        )

        if (DeviceTransmissionResponse.isSuccess) {
          // this._checkLatestStatus()
        } else {
          if (DeviceTransmissionResponse.RemoteServiceResponse?.statusCode === STATUS_CODE_NATIVE_SERVICE_BIND_FAILED) {
            this._failedReason = TaskError.NATIVE_SERVICE_BIND_FAILED
          } else {
            this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
          }

          this._taskScheduler?.setTaskError(this)
          this.clearTimer()
        }
      }
    } catch (error) {
      console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'error:resume', error)
      this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED

      this._handleError(error)
    }
  }

  async onRemove(): Promise<boolean> {
    if (this.#isTransmissionToOCS) {
      this.markAsDeleted()
      this._requestCancelToken?.cancel()
      this._requestCancelToken = undefined

      return true
    }

    const device = this._getDevice()

    if (!device) {
      this._terminateDueToDeviceNotFound()

      return true
    }

    if (!this.canChangeState) return true

    this.canChangeState = false
    this._taskScheduler?.updateTask(this)
    this.markAsDeleted()
    this.clearTimer()

    try {
      const DeviceTransmissionResponse = await DeviceTransmissionService.cancel(
        device,
        this._noticeId,
        false,
        TransmissionTaskType.UPLOAD
      )

      console.log(
        `[Transmission] ${new Date().toLocaleString()}  fileName:`,
        this._fileName,
        'DeviceTransmissionResponse:cancel',
        DeviceTransmissionResponse
      )

      if (!DeviceTransmissionResponse.isSuccess) {
        if (DeviceTransmissionResponse.RemoteServiceResponse?.statusCode === STATUS_CODE_NATIVE_SERVICE_BIND_FAILED) {
          this._failedReason = TaskError.NATIVE_SERVICE_BIND_FAILED
        } else {
          this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
        }

        this._taskScheduler?.setTaskError(this)

        return true
      }

      return true
    } catch (error) {
      console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'error:cancel', error)
      this._handleError(error)

      return true
    }
  }

  /**
   * 根据通知消息更新任务状态
   * @param transmissionNotice
   * @param isFreezed
   * @returns
   */
  updateInfo(transmissionNotice: TransmissionNoticeData, isFreezed?: boolean): void {
    this.canChangeState = true
    const { state, fileSize, transmitted, reason } = transmissionNotice

    if ([TaskState.SUCCESS, TaskState.FAILED, TaskState.PAUSE].includes(this.state)) return

    switch (state) {
      case TransmissionState.DONE_SUCCEED:
        if (fileSize === ILLEGAL_FILE_SIZE) return
        this.#downloadToDeviceTransmitted = fileSize
        this.#downloadFromDeviceTotal = fileSize
        this._fileUploadListener?.onSuccess(this._deviceId, this._targetPath, this._fileName)
        this._taskScheduler?.setTaskSucceed(this)
        this.clearTimer()
        this._checkLatestStatus.cancel()

        break
      case TransmissionState.DONE_FAILED:
        console.log(
          `[Transmission] ${new Date().toLocaleString()}  fileName:`,
          this._fileName,
          'device notifies  task has failed:',
          state
        )

        this._fileUploadListener?.onError(this._deviceId, this._targetPath, this._fileName, 'error')
        this._failedReason = TaskError.DEVICE_NOTIFIED_FAILURE_MESSAGE
        this._taskScheduler?.setTaskError(this)
        this.clearTimer()
        this._checkLatestStatus.cancel()

        break
      case TransmissionState.PAUSE:
        this.clearTimer()
        this._checkLatestStatus.cancel()

        if (reason === DeviceTaskError.DEVICE_CANCEL) {
          this._failedReason = DeviceTaskError.DEVICE_CANCEL
          this._handleError(DeviceTaskError.DEVICE_CANCEL)

          return
        }
        this._taskScheduler?.pauseTask(this)

        break
      case TransmissionState.ON_GOING:
        if (this._state === TaskState.FAILED || this._state === TaskState.SUCCESS) return

        this.#downloadFromDeviceTotal = fileSize

        if (transmitted >= this.#downloadToDeviceTransmitted) {
          this.#downloadToDeviceTransmitted = transmitted
        }
        this._fileUploadListener?.onProgressChanged?.(
          this.#downloadToDeviceTransmitted,
          this.#downloadFromDeviceTotal,
          this._deviceId,
          this.#isTransmissionToOCS
        )
        this._taskScheduler?.updateTask(this, isFreezed)
        if (this.#isTransmissionToOCS) return
        this._checkLatestStatus()

        break
      default:
        break
    }
  }

  onStart(id: string): void {
    this.#multipartId = id
    this._taskScheduler?.updateTask(this)
  }

  onProgressChanged(transmitted: number, total: number): void {
    if (this._state === TaskState.FAILED || this._state === TaskState.SUCCESS) return

    if (transmitted <= this.#uploadToOcsTransmitted) return
    this.#uploadToOcsTotal = total
    this.#uploadToOcsTransmitted = transmitted
    this._taskScheduler?.updateTask(this)
    this._fileUploadListener?.onProgressChanged?.(
      this.#uploadToOcsTransmitted,
      this.#uploadToOcsTotal,
      this._deviceId,
      this.#isTransmissionToOCS
    )
  }

  //上传到ocs后触发
  async onCompleted(fileId: string): Promise<void> {
    this._requestCancelToken = undefined
    this.#fileId = fileId
    const device = this._getDevice()

    if (!device) {
      this._terminateDueToDeviceNotFound()

      return
    }

    try {
      TransmissionDataBusProxy.getInstance()
        .updateNoticeById(this.noticeId, { cloudPath: fileId })
        .then((notice) => {
          console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'notice:', notice)
        })
        .catch((err) => {
          console.log(`[Transmission] ${new Date().toLocaleString()}  fileName:`, this._fileName, 'notice error:', err)
        })

      const DeviceTransmissionResponse = await DeviceTransmissionService.downloadFile(
        device,
        fileId,
        this._targetPath + this._fileName,
        this.noticeId
      )

      console.log(
        `[Transmission] ${new Date().toLocaleString()}  fileName:`,
        this._fileName,
        'DeviceTransmissionResponse:downloadFile',
        DeviceTransmissionResponse
      )

      if (DeviceTransmissionResponse.isSuccess) {
        this.#isTransmissionToOCS = false
        // this._checkLatestStatus()
        this.setTimer()
      } else {
        if (DeviceTransmissionResponse.RemoteServiceResponse?.statusCode === STATUS_CODE_NATIVE_SERVICE_BIND_FAILED) {
          this._failedReason = TaskError.NATIVE_SERVICE_BIND_FAILED
        } else {
          this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED
        }

        this._taskScheduler?.setTaskError(this)
      }
    } catch (error) {
      console.log(
        `[Transmission] ${new Date().toLocaleString()}  fileName:`,
        this._fileName,
        'error:downloadFile',
        error
      )
      this._failedReason = TaskError.NOTIFICATION_DEVICE_FAILED

      this._handleError(error)
    }
  }

  protected _getCompletionRation(): number {
    let completionRatio = 0

    if (this.#uploadToOcsTotal + this.#downloadFromDeviceTotal > 0) {
      completionRatio = Math.floor(
        ((this.#uploadToOcsTransmitted + this.#downloadToDeviceTransmitted) /
          (this.#uploadToOcsTotal + this.#downloadFromDeviceTotal)) *
          100
      )
    }

    return completionRatio
  }
}
